import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-grid-system";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { updateProductFields } from "../../../server/Product";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input, { Select } from "../../containers/InputForm";
import { getDictByKind } from "../../../server/Category";
import { getSuppliers } from "../../../server/Supplier";
import useFamilyProductUpdate, {
  applySequentially,
} from "../productPage/useFamilyProductUpdate";
import { buildNestedFieldUpdate } from "../../../utils/productExpirationDisplay";
import showAlert from "../../../server/ShowAlert";

const schema = yup.object({}).required();

const isDirtyField = (dirtyFields, name) => {
  if (!dirtyFields || !name) return false;
  if (dirtyFields[name]) return true;
  const parts = String(name)
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".");
  let current = dirtyFields;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return false;
    current = current[part];
  }
  return Boolean(current);
};

const Logistica = ({ onSaved }) => {
  const product = useSelector((state) => state.selectedProduct.product);
  const stationId = useSelector((state) => state.user.stationId);

  const {
    register,
    formState: { errors, dirtyFields, isDirty },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: { ...product },
    resolver: yupResolver(schema),
  });

  const storageConditionOptions = [
    { Id: "Frozen", Name: "קפוא" },
    { Id: "Refrigerated", Name: "מקורר" },
    { Id: "Room temperature", Name: "טמפרטורת חדר" },
    { Id: "Dry", Name: "יבש" },
    { Id: "Ambient", Name: "סביבה" },
    { Id: "Chilled", Name: "מצונן" },
    { Id: "Deep Frozen", Name: "קפוא עמוק" },
  ];

  const [brand, setBrand] = useState([]);
  const [subBrand, setSubBrand] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const { requestUpdate, familyDialog } = useFamilyProductUpdate();

  useEffect(() => {
    const fetchFromDict = (kind, setKind) => {
      getDictByKind(kind)
        .then((res) => setKind(res))
        .catch((error) => console.error("Error fetching dictionary:", error));
    };
    fetchFromDict("sub-brand", setSubBrand);
    getSuppliers()
      .then((res) => setBrand(res))
      .catch((error) => console.error("Error fetching suppliers:", error));
  }, []);

  useEffect(() => {
    if (!product) return;
    reset({ ...product });
    if (subBrand.length && product?.subBrand?.id)
      setValue("subBrand", product.subBrand.id);
    if (brand.length && product?.brand?.id) setValue("brand", product.brand.id);
  }, [product, subBrand, brand, setValue, reset]);

  const resetFormToProduct = () => {
    reset({ ...product });
    if (subBrand.length && product?.subBrand?.id)
      setValue("subBrand", product.subBrand.id);
    if (brand.length && product?.brand?.id) setValue("brand", product.brand.id);
  };

  const buildDirtyLogisticsChanges = (values) => {
    const change = {};

    const assignChange = (fieldId, patch) => {
      if (!isDirtyField(dirtyFields, fieldId)) return;
      Object.assign(change, patch);
    };

    assignChange("userCompany", { userCompany: values.userCompany });
    const brandPatch = (() => {
      const selectedId = values.brand;
      if (!selectedId) return { brand: null };
      const selected = brand.find((item) => item.id === selectedId);
      return selected
        ? { brand: { id: selected.id, name: selected.name } }
        : {};
    })();
    assignChange("brand", brandPatch);
    const subBrandPatch = (() => {
      const selectedId = values.subBrand;
      if (!selectedId) return { subBrand: null };
      const selected = subBrand.find((item) => item.id === selectedId);
      return selected
        ? { subBrand: { id: selected.id, name: selected.name } }
        : {};
    })();
    assignChange("subBrand", subBrandPatch);
    assignChange("Action", { Action: values.Action });

    const nestedFields = [
      "logisticsDetails.masterCartonQuantity",
      "logisticsDetails.manufacturerPackQuantity",
      "logisticsDetails.miniPackQuantity",
      "logisticsDetails.manufacturerPackageSize",
      "logisticsDetails.itemHeight",
      "logisticsDetails.volumeLevel",
      "logisticsDetails.storageCondition",
      "logisticsDetails.shelfLifeDays",
      "logisticsDetails.minimumExpirationDays",
      "logisticsDetails.separateProduct",
      "logisticsDetails.expiryAlert",
    ];

    nestedFields.forEach((fieldId) => {
      if (!isDirtyField(dirtyFields, fieldId)) return;
      const path = fieldId.replace(/^logisticsDetails\./, "");
      const value = values.logisticsDetails?.[path];
      const normalized =
        fieldId === "logisticsDetails.separateProduct" ||
        fieldId === "logisticsDetails.expiryAlert"
          ? Boolean(value)
          : value;
      const nested = buildNestedFieldUpdate(fieldId, normalized);
      change.logisticsDetails = {
        ...(change.logisticsDetails || {}),
        ...(nested.logisticsDetails || {}),
      };
    });

    return change;
  };

  const saveLogisticsChanges = handleSubmit(async (values) => {
    if (!product?.productId || !isDirty) return;

    const change = buildDirtyLogisticsChanges(values);
    if (Object.keys(change).length === 0) return;

    requestUpdate(async (productsToChange) => {
      setIsSaving(true);
      try {
        await applySequentially(productsToChange, async (prod) => {
          await updateProductFields(stationId, prod.productId, change);
        });
        reset(values);
        showAlert({ msgTitle: "השינויים נשמרו בהצלחה", msgType: "success" });
        onSaved?.(productsToChange, {
          familyPatch: change,
          mainProductId: product.productId,
        });
        return true;
      } catch (error) {
        const apiMessage =
          error?.response?.data?.error?.error ?? error?.response?.data?.error;
        showAlert({
          msgTitle:
            typeof apiMessage === "string"
              ? apiMessage
              : "אירעה שגיאה בעדכון פרטי הלוגיסטיקה",
          msgType: "error",
        });
        resetFormToProduct();
        return false;
      } finally {
        setIsSaving(false);
      }
    }, resetFormToProduct);
  });

  return (
    <form className="product-details-edit" onSubmit={saveLogisticsChanges}>
      <Container>
        <Row>
          <InputCol
            sm={4}
            name="userCompany"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            label="יצרן"
          />
          <SelectCol
            sm={4}
            name="brand"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            options={brand}
            label="ספק"
            value={watch("brand")}
            displayLabel={product?.brand?.name || ""}
          />
          <InputCol
            sm={4}
            name="logisticsDetails.masterCartonQuantity"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="כמות בארגז"
          />
          <InputCol
            errors={errors}
            label="כמות במארז יצרן"
            name="logisticsDetails.manufacturerPackQuantity"
            dirtyFields={dirtyFields}
            type="number"
            sm={4}
            register={register}
          />
          <InputCol
            errors={errors}
            label="כמות במיני מארז"
            name="logisticsDetails.miniPackQuantity"
            dirtyFields={dirtyFields}
            type="number"
            sm={4}
            register={register}
          />
          <InputCol
            errors={errors}
            label="מידות אריזות יצרן"
            name="logisticsDetails.manufacturerPackageSize"
            dirtyFields={dirtyFields}
            type="number"
            sm={4}
            register={register}
          />
          <InputCol
            sm={4}
            name="logisticsDetails.itemHeight"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="גובה פריט"
          />
          <InputCol
            sm={4}
            name="logisticsDetails.volumeLevel"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="רמת נפח"
          />
          <InputCol
            sm={4}
            name="Action"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="רמת פעילות"
          />
          <SelectCol
            sm={4}
            name="logisticsDetails.storageCondition"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            options={storageConditionOptions}
            label="טמפרטורת אחסון"
            value={watch("logisticsDetails.storageCondition")}
          />
          <InputCol
            sm={4}
            name="logisticsDetails.shelfLifeDays"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="חיי מדף בימים"
          />
          <InputCol
            sm={4}
            name="logisticsDetails.minimumExpirationDays"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="מינימום ימי פג תוקף"
          />
          <div className="check-box">
            <div className="checkbox-style">
              <Input
                name="logisticsDetails.separateProduct"
                errors={errors}
                dirtyFields={dirtyFields}
                register={register}
                type="checkbox"
                label=" מוצר מופרד"
              />
            </div>
            <div className="checkbox-style">
              <Input
                name="logisticsDetails.expiryAlert"
                errors={errors}
                dirtyFields={dirtyFields}
                register={register}
                type="checkbox"
                label=" התראת פג תוקף"
              />
            </div>
          </div>
        </Row>

        <div className="product-details-edit__actions">
          <button
            type="submit"
            className="ui-btn-brand product-details-edit__save-btn"
            disabled={!isDirty || isSaving}
          >
            {isSaving ? "שומר..." : "שמור שינויים"}
          </button>
        </div>
      </Container>
      {familyDialog}
    </form>
  );
};

const InputCol = ({ name, errors, label, type, register, sm, dirtyFields }) => (
  <Col sm={sm}>
    <Input
      name={name}
      errors={errors}
      dirtyFields={dirtyFields}
      register={register}
      label={label}
      type={type}
    />
  </Col>
);

const SelectCol = ({
  name,
  errors,
  label,
  register,
  sm,
  options,
  onChange,
  value,
  dirtyFields,
  displayLabel,
}) => {
  const { onChange: registerOnChange, ...registerRest } = register(name);
  return (
    <Col sm={sm}>
      <Select
        name={name}
        errors={errors}
        {...registerRest}
        options={options}
        label={label}
        onChange={(e) => {
          registerOnChange?.(e);
          onChange?.(e);
        }}
        value={value}
        displayLabel={displayLabel}
        dirtyFields={dirtyFields}
      />
    </Col>
  );
};

export default Logistica;
